import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordValidator } from 'record-it-utils/password/PasswordValidator/PasswordValidator';
import { ButtonStandard, ButtonStandardHoverEffectType, ButtonStandardType } from '../../button/ButtonStandard/ButtonStandard';
import { CardBody } from '../../card/CardBody/CardBody';
import { CardHeader, HorizontalAlignment } from '../../card/CardHeader/CardHeader';
import { CardStandard, CardStandardProps } from '../../card/CardStandard/CardStandard';
import { useEventCallback } from '../../hooks/useEventCallback';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import { TextInput, TextInputType } from '../../input/TextInput/TextInput';
import { LoadingOverlay } from '../../misc/LoadingOverlay/LoadingOverlay';
import { MessageBox, MessageBoxType } from '../../misc/MessageBox/MessageBox';
import { PasswordRulesBox } from '../../misc/PasswordRulesBox/PasswordRulesBox';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import { FormGroup } from '../FormGroup/FormGroup';
import styles from './ResetPasswordForm.module.css';
export type ResetPasswordFormProps = PropsWithoutChildren<CardStandardProps> & {
  resetPassword: (password: string) => Promise<void>;
  onCancel: () => void;
  onFinish: () => void;
};
export const ResetPasswordForm: FunctionComponent<ResetPasswordFormProps> = React.memo(props => {
  const $ = _c(45);
  const {
    className,
    resetPassword,
    onCancel,
    onFinish,
    ...remainingProps
  } = prepareProps({
    props,
    ignoreChildren: true
  });
  const [password, setPassword] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resettingPasswordFailed, setResettingPasswordFailed] = useState(false);
  const {
    t
  } = useTranslation("record-it-components");
  const notificationManager = useNotificationManager();
  const handleResetPasswordButtonClick = useEventCallback(() => {
    if (isResettingPassword) {
      return;
    }
    setIsResettingPassword(true);
    setResettingPasswordFailed(false);
    resetPassword(password).then(() => {
      notificationManager.success({
        title: t("form.ResetPasswordForm.successNotification.title"),
        message: t("form.ResetPasswordForm.successNotification.message")
      });
      onFinish();
    }).catch(e => {
      console.error(e);
      setResettingPasswordFailed(true);
    }).finally(() => {
      setIsResettingPassword(false);
    });
  });
  let t0;
  if ($[0] !== password) {
    const validator = new PasswordValidator();
    t0 = validator.validate(password);
    $[0] = password;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const passwordValidity = t0;
  const T0 = CardStandard;
  const t1 = classes(className, styles.card);
  let t2;
  if ($[2] !== t) {
    t2 = t("form.ResetPasswordForm.title");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <CardHeader textAlignment={HorizontalAlignment.Center}>{t2}</CardHeader>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== resettingPasswordFailed || $[7] !== t) {
    t4 = resettingPasswordFailed ? <MessageBox type={MessageBoxType.Error} className={styles.resettingPasswordErrorMessageBox} data-testid="ResetPasswordForm.ResettingPasswordFailedText">{t("form.ResetPasswordForm.resettingPasswordFailedText")}</MessageBox> : null;
    $[6] = resettingPasswordFailed;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("form.ResetPasswordForm.passwordLabel");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== password || $[12] !== setPassword || $[13] !== t5) {
    t6 = <TextInput label={t5} type={TextInputType.Password} value={password} onInput={setPassword} data-testid="ResetPasswordForm.PasswordTextInput" />;
    $[11] = password;
    $[12] = setPassword;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== passwordValidity) {
    t7 = <PasswordRulesBox passwordValidity={passwordValidity} className={styles.passwordRuleBox} />;
    $[15] = passwordValidity;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <FormGroup>{t6}{t7}</FormGroup>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = !passwordValidity.valid;
  let t10;
  if ($[20] !== t) {
    t10 = t("form.ResetPasswordForm.resetPassword");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== handleResetPasswordButtonClick || $[23] !== t10 || $[24] !== t9) {
    t11 = <div className={styles.resetPasswordButtonWrapper}><ButtonStandard type={ButtonStandardType.Primary} onClick={handleResetPasswordButtonClick} disabled={t9} data-testid="ResetPasswordForm.ResetPasswordButton">{t10}</ButtonStandard></div>;
    $[22] = handleResetPasswordButtonClick;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t) {
    t12 = t("form.ForgotPasswordForm.cancel");
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== onCancel || $[29] !== t12) {
    t13 = <div className={styles.cancelButtonWrapper}><ButtonStandard type={ButtonStandardType.TextSecondary} hoverEffectType={ButtonStandardHoverEffectType.HalfTransparent} onClick={onCancel} data-testid="ResetPasswordForm.CancelButton">{t12}</ButtonStandard></div>;
    $[28] = onCancel;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== isResettingPassword) {
    t14 = <LoadingOverlay active={isResettingPassword} data-testid="ResetPasswordForm.LoadingOverlay" />;
    $[31] = isResettingPassword;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t11 || $[34] !== t13 || $[35] !== t14 || $[36] !== t4 || $[37] !== t8) {
    t15 = <CardBody>{t4}{t8}{t11}{t13}{t14}</CardBody>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t14;
    $[36] = t4;
    $[37] = t8;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== T0 || $[40] !== remainingProps || $[41] !== t1 || $[42] !== t15 || $[43] !== t3) {
    t16 = <T0 {...remainingProps} className={t1}>{t3}{t15}</T0>;
    $[39] = T0;
    $[40] = remainingProps;
    $[41] = t1;
    $[42] = t15;
    $[43] = t3;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
});